/**
 * Resolves Python `decimal.Decimal` serialization artifacts in API responses.
 *
 * The backend sometimes serializes `decimal.Decimal` values as:
 *   `{ __reduce__: [{ "py/type": "decimal.Decimal" }, ["30.00"]] }`
 * instead of a plain number. This utility deep-walks a response object and
 * replaces every such artifact with the parsed numeric value.
 *
 * Applied at the API boundary (useTileData, preview queries) so no downstream
 * component needs to know about the serialization quirk.
 */

function isReduceDecimal(
  value: unknown,
): value is {__reduce__: [unknown, [string, ...unknown[]]]} {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.__reduce__) &&
    Array.isArray(obj.__reduce__[1]) &&
    typeof obj.__reduce__[1][0] === 'string'
  );
}

export function normalizeDecimalValues<T>(value: T): T {
  if (value == null) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeDecimalValues) as T;
  }
  if (typeof value === 'object') {
    if (isReduceDecimal(value)) {
      const numStr = (value as {__reduce__: [unknown, [string]]})
        .__reduce__[1][0];
      const n = Number(numStr);
      return (Number.isFinite(n) ? n : null) as T;
    }
    const normalized: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      normalized[k] = normalizeDecimalValues(v);
    }
    return normalized as T;
  }
  return value;
}
